/**
 * Nederlandse teksten voor het rapport (scherm 9), in de jij-vorm.
 *
 * Goedkeuren is versturen. Dat staat hier met zoveel woorden, want het is
 * onomkeerbaar. Termen volgen `docs/woordenlijst.md`.
 */
export const reportCopy = {
    screenTitle: 'Rapport',

    eyebrowLoading: 'Rapport',
    eyebrowNone: 'nog geen rapport',
    eyebrowGenerating: (version: number) => `versie ${version} · wordt gegenereerd`,
    eyebrowFailed: (version: number) => `versie ${version} · genereren mislukt`,
    eyebrowConcept: (version: number, minutes: number) =>
        minutes > 0
            ? `concept · versie ${version} · gegenereerd ${minutes} min na opname`
            : `concept · versie ${version}`,
    eyebrowApproved: (version: number) => `goedgekeurd · versie ${version} · nog niet bezorgd`,
    /** Een correctie, vastgesteld zonder bericht: de klant ziet haar via zijn eigen link. */
    eyebrowApprovedBehindLink: (version: number) =>
        `vastgesteld · versie ${version} · via dezelfde link bij de klant`,
    eyebrowSent: (version: number, at: string) => `verstuurd · versie ${version} · ${at}`,

    pdf: 'PDF downloaden',
    pdfView: 'PDF bekijken',
    pdfBusy: 'PDF wordt gemaakt…',
    pdfWaiting: 'De PDF kan pas gemaakt worden als het concept klaar is.',
    pdfUnavailable: 'De PDF kan nu even niet gemaakt worden. Probeer het over een minuut opnieuw.',
    /**
     * Het maken van PDF's lukt bij Smartkeur nu helemaal niet. Opnieuw klikken helpt
     * dan niet, en de klant kan er zelf niets aan doen: dat zeggen we, in gewone taal.
     */
    pdfNotConfigured:
        'De PDF kan nu niet gemaakt worden. Smartkeur lost dit op; daarna komt de PDF vanzelf. Je hoeft zelf niets te doen.',
    /* Wat er onder de melding staat als er al meer rapporten op hun PDF wachten. */
    pdfBacklog: (waiting: number) =>
        waiting === 1
            ? 'Er wacht nog 1 verstuurd rapport op zijn PDF. Smartkeur maakt hem vanzelf zodra het weer kan.'
            : `Er wachten nog ${waiting} verstuurde rapporten op hun PDF. Smartkeur maakt ze vanzelf zodra het weer kan.`,
    generate: 'Concept genereren',
    regenerate: 'Opnieuw genereren',
    /**
     * Eén knop voor een nieuwe versie van een rapport dat al vaststaat. In de
     * bevestiging kies je hoe die begint: met je eigen teksten, of opnieuw uit de opname.
     */
    newVersion: 'Nieuwe versie',
    newVersionTitle: 'Nieuwe versie maken',
    newVersionBody: (version: number) =>
        `Tot je de nieuwe versie vaststelt, houdt de klant versie ${version} met zijn eigen link. Daarna toont dezelfde link vanzelf de nieuwe versie.`,
    newVersionChoice: 'Waarmee begint de nieuwe versie?',
    newVersionKeep: 'Eigen teksten houden',
    newVersionKeepHint: (version: number) =>
        `Je begint met precies versie ${version}: dezelfde hoofdstukken, je eigen teksten en dezelfde bedragen.`,
    newVersionRebuild: 'Opnieuw opbouwen uit de opname',
    newVersionRebuildHint: (version: number) =>
        `Smartkeur maakt het rapport opnieuw uit de opname, met wat daar veranderd is; je eigen teksten blijven bij versie ${version}.`,
    newVersionRebuildSurveyOpen: 'Dit kan pas als de opname opnieuw is ingediend.',
    newVersionConfirm: 'Nieuwe versie maken',
    newVersionCancel: 'Annuleren',
    /**
     * Goedkeuren en versturen is één klik bij de eerste versie: de bevestiging stuurt
     * het rapport naar de klant. Bij een versie die de klant al via zijn link heeft,
     * verstuurt de knop niets en heet hij daarom "Versie N vaststellen".
     */
    approve: 'Goedkeuren en versturen',
    /** Het menu met de rest: lezen, de PDF en de klantpagina. */
    more: 'Meer',
    surveyOpenTitle: 'De opname staat weer open',
    /** Foto's die de server niet kon verwerken staan in geen rapport; dat zeggen we hardop. */
    photosFailedTitle: (count: number) =>
        count === 1
            ? '1 foto kon niet worden verwerkt'
            : `${count} foto's konden niet worden verwerkt`,
    photosFailedConcept: (count: number) =>
        count === 1
            ? 'Hij staat nog niet in dit concept. Smartkeur probeert hem automatisch opnieuw; lukt dat, dan komt hij vanzelf in het rapport.'
            : 'Ze staan nog niet in dit concept. Smartkeur probeert ze automatisch opnieuw; lukt dat, dan komen ze vanzelf in het rapport.',
    photosFailedReleased: (count: number) =>
        count === 1
            ? 'Hij staat niet in deze versie. Smartkeur probeert hem automatisch opnieuw; lukt dat, maak dan een nieuwe versie om hem toe te voegen.'
            : 'Ze staan niet in deze versie. Smartkeur probeert ze automatisch opnieuw; lukt dat, maak dan een nieuwe versie om ze toe te voegen.',
    surveyOpenBody:
        'Goedkeuren en opnieuw genereren kan pas als de opname opnieuw is ingediend. Daarna staat hier vanzelf het concept dat bij de opname past, en dat keur je goed.',
    surveyOpenReadOnly:
        'Dit concept past misschien niet meer bij de opname. Zodra de opname opnieuw is ingediend, staat hier het concept dat erbij past.',
    /** De opname staat weer open, maar de klant heeft al een versie. */
    surveyOpenReleasedBody:
        'Het verstuurde rapport blijft staan tot je een nieuwe versie vaststelt; daarna ziet de klant die via dezelfde link. Zodra de opname opnieuw is ingediend, staat hier het concept van de nieuwe versie, met je eigen teksten erin.',
    resend: 'Opnieuw versturen',
    clientPage: 'Klantpagina',

    noneTitle: 'Er is nog geen rapport voor deze keuring',
    noneBody:
        'Een concept ontstaat vanzelf zodra de inspecteur de opname indient. Is dat al gebeurd en staat hier nog niets, laat het dan nu maken.',
    noneReadOnly: 'Zodra de opname is ingediend, verschijnt hier het concept.',

    generatingTitle: 'Het concept wordt gemaakt',
    generatingBody:
        'Het rapport wordt opgebouwd uit de opname. Dat duurt meestal minder dan een minuut; dit scherm ververst vanzelf.',
    generatingAutoSendBody:
        'Het rapport wordt opgebouwd uit de opname; dit scherm ververst vanzelf. Je bedrijf verstuurt zonder nalezen: kwam deze versie vanzelf na het indienen van de opname, dan gaat hij daarna meteen naar de klant. Een versie die je hier maakt, blijft een concept.',

    failedTitle: 'Het concept kon niet gemaakt worden',
    failedBody: 'Probeer het opnieuw. Lukt het dan nog niet, neem dan contact op met Smartkeur.',

    notGeneratable: {
        no_inspection:
            'Er is nog geen opname voor deze keuring, dus er valt nog niets te rapporteren.',
        no_template_version:
            'Bij deze keuring hoort geen vragenlijst, dus er kan geen rapport uit volgen.',
        order_cancelled: 'Deze keuring is geannuleerd; daar hoort geen rapport meer bij.',
        inspection_not_submitted:
            'De opname is nog niet ingediend. Het concept volgt vanzelf zodra de inspecteur hem indient.',
        inspection_reopened:
            'De opname staat weer open. Het concept van de nieuwe versie volgt vanzelf zodra de opname opnieuw is ingediend.',
    } as Record<string, string>,

    regenerateTitle: 'Nieuwe versie maken',
    regenerateBody: (version: number) =>
        `Het rapport wordt opnieuw opgebouwd uit de opname. Je aangepaste teksten blijven bij versie ${version}; de nieuwe versie begint met de gegenereerde teksten.`,
    regenerateStaysConcept: (version: number) =>
        `De nieuwe versie blijft een concept tot je hem vaststelt; tot die tijd houdt de klant versie ${version} met zijn eigen link.`,
    regenerateConfirm: 'Nieuwe versie maken',
    regenerateCancel: 'Annuleren',

    confirmTitle: 'Goedkeuren is versturen',
    confirmBody:
        'Zodra je goedkeurt gaat het rapport meteen naar de ontvangers, met de link naar de klantpagina. Terugdraaien kan niet.',
    confirmRecipients: 'Gaat naar',
    confirmSend: (n: number) => `Versturen naar ${n} ${n === 1 ? 'ontvanger' : 'ontvangers'}`,
    confirmOnlyRelease: 'Alleen vaststellen, ik verstuur zelf',
    confirmOnlyReleaseHint:
        'Dan staat het rapport vast en krijg je de link om te kopiëren; er gaat niets de deur uit en er komt geen factuur mee. Versturen vanuit Smartkeur kan daarna nog steeds.',
    confirmCancel: 'Annuleren',
    confirmNoRecipients:
        'Er staat nog geen ontvanger met een e-mailadres klaar. Vul het e-mailadres van de klant in bij Klant bewerken; het rapport neemt het meteen over.',
    editClient: 'Klant bewerken',
    confirmWithoutEmail: (name: string) => `${name} krijgt niets: geen e-mailadres`,

    /** Vaststellen van een nieuwe versie van een rapport dat de klant al heeft. */
    correctionTitle: (version: number) => `Versie ${version} vaststellen`,
    correctionBody: (version: number, released: number) =>
        `Zodra je vaststelt, toont de link van de klant versie ${version} in plaats van versie ${released}, ook als pdf. De klant hoeft niets te doen: het is dezelfde link.`,
    correctionNotify: 'Stuur de klant een bericht dat er een bijgewerkte versie is',
    correctionNotifyTo: (n: number) =>
        n === 0
            ? 'Er staat geen ontvanger met een e-mailadres klaar.'
            : `Per e-mail naar ${n} ${n === 1 ? 'ontvanger' : 'ontvangers'}. Staat dit uit, dan gaat er niets de deur uit.`,
    correctionConfirm: (version: number) => `Versie ${version} vaststellen`,

    sentNotice: (at: string) => `Verstuurd op ${at}. Deze versie staat vast.`,

    releasedTitle: (version: number) => `De klant heeft versie ${version}`,
    releasedBody: (version: number, newest: number, until: string) =>
        `Versie ${version} is verstuurd en blijft voor de klant open met zijn link, die werkt tot ${until}. Versie ${newest} gaat pas naar de klant als je hem vaststelt.`,
    releasedWithoutLink: (version: number, newest: number) =>
        `Versie ${version} is verstuurd, maar de klant heeft geen werkende link meer; op de klantpagina maak je een nieuwe. Versie ${newest} gaat pas naar de klant als je hem goedkeurt.`,
    releasedClientPage: (version: number) => `Klantpagina van versie ${version}`,
    approvedNotice:
        'Goedgekeurd, maar nog bij niemand bezorgd. Verstuur het rapport opnieuw; de link blijft dezelfde.',
    approvedBehindLinkNotice: (version: number, replaced: number) =>
        `Versie ${version} staat bij de klant, via dezelfde link als versie ${replaced}. Er is geen bericht verstuurd.`,

    linkTitle: 'De rapportlink van deze keuring',
    linkBody: (until: string) =>
        `Dit is de vaste link naar het rapport; hij werkt tot ${until}. Werk je het rapport later bij, dan toont dezelfde link vanzelf de nieuwste versie.`,
    linkWithoutUrl:
        'Deze versie heeft geen werkende link meer. Maak er een nieuwe voor op de klantpagina.',
    linkNotSentYet:
        'Er is nog niets verstuurd vanuit Smartkeur. Kopieer de link en zet hem in je eigen e-mail, of laat Smartkeur hem versturen.',
    linkCodeRequired:
        'Deze link vraagt om een toegangscode. Geef die apart door; hij staat niet in de link.',

    resultSentTitle: 'Het rapport is verstuurd',
    resultReleasedTitle: 'Het rapport staat vast',
    resultReleased:
        'Er is niets verstuurd vanuit Smartkeur. Kopieer de link hieronder en zet hem in je eigen e-mail. Versturen vanuit Smartkeur kan daarna nog steeds met "Opnieuw versturen", ook met de factuur erbij.',
    resultPartialTitle: 'Het rapport is goedgekeurd, maar niet bij iedereen bezorgd',
    resultCorrectionTitle: (version: number) => `Versie ${version} staat bij de klant`,
    resultCorrectionQuiet:
        'De klant ziet de nieuwe versie via dezelfde link, ook als pdf. Er is geen bericht verstuurd.',
    resultCorrectionNotified: (n: number) =>
        `De klant ziet de nieuwe versie via dezelfde link. ${n} ${n === 1 ? 'ontvanger kreeg' : 'ontvangers kregen'} een bericht dat er een bijgewerkte versie is.`,
    resultResentTitle: 'Opnieuw verstuurd, met een nieuwe link: de oude werkte niet meer',
    resultSent: (n: number, via: string) =>
        `${n} ${n === 1 ? 'ontvanger kreeg' : 'ontvangers kregen'} de link${via ? ` per ${via}` : ''}.`,
    resultFailed: (n: number) =>
        `${n} ${n === 1 ? 'ontvanger kreeg' : 'ontvangers kregen'} hem niet. Verstuur het rapport opnieuw.`,
    resultLink: 'Link naar de klantpagina',
    resultValidUntil: (date: string) => `Geldig tot ${date}.`,
    resultCode: 'Toegangscode',
    resultCodeWarning:
        'Deze code zie je nu één keer. Ontvangers met een telefoonnummer krijgen hem per sms, los van de link in de mail. Kwijt? Maak op de klantpagina een nieuwe code.',
    resultCodeWithoutPhone: (names: string) =>
        `Geen sms, want geen telefoonnummer: ${names}. Geef de code zelf door.`,
    resultClose: 'Klaar',
    channels: { email: 'e-mail', sms: 'sms' } as Record<string, string>,

    resendDone: (n: number) =>
        `Opnieuw verstuurd naar ${n} ${n === 1 ? 'ontvanger' : 'ontvangers'}.`,
    resendPartial: (sent: number, failed: number) =>
        `Opnieuw verstuurd naar ${sent}; ${failed} ${failed === 1 ? 'ontvanger kreeg' : 'ontvangers kregen'} hem niet.`,

    /** De bevestiging van "Opnieuw versturen": naar wie, en of de factuur meegaat. */
    sendTitle: 'Rapport versturen',
    sendBody:
        'Het rapport gaat naar deze ontvangers, met dezelfde link als eerder. Werkt die link niet meer, dan maakt Smartkeur een nieuwe.',
    sendConfirm: (n: number) => `Versturen naar ${n} ${n === 1 ? 'ontvanger' : 'ontvangers'}`,

    /**
     * Factuur meesturen, in elke dialoog waarin het rapport naar de klant gaat. Wat er
     * met de factuur gebeurt staat er met zoveel woorden bij: een concept wordt
     * definitief, met een nummer volgens de reeks, en dat kan niet terug.
     */
    /**
     * "Taal voor de klant" in de verstuurdialogen: alleen als de server vertaalt. Wat
     * de klant krijgt, staat er in één zin bij; de makelaar en de adviseur houden het
     * Nederlands.
     */
    clientLanguage: {
        label: 'Taal voor de klant',
        description:
            'De klant krijgt de mail in deze taal, en de link opent het rapport automatisch vertaald. Het Nederlands blijft leidend; makelaar en adviseur krijgen het Nederlands.',
        names: {
            nl: 'Nederlands',
            en: 'Engels',
            de: 'Duits',
            fr: 'Frans',
            es: 'Spaans',
            pl: 'Pools',
            tr: 'Turks',
            ar: 'Arabisch',
        } as Record<string, string>,
    },
    invoice: {
        label: 'Factuur',
        toggle: 'Factuur meesturen',
        loading: 'Factuur opzoeken…',
        unknown: 'Of de factuur mee kan, is nu niet bekend. Het rapport gaat zonder factuur.',
        mail: 'De klant krijgt één mail: de link naar het rapport, de factuur als pdf-bijlage met het bedrag en hoe hij betaalt, en je eigen bericht.',
        none: 'Er is nog geen factuur. Smartkeur maakt haar bij versturen uit de diensten van deze keuring en maakt haar definitief: ze krijgt het volgende factuurnummer en de datum van vandaag.',
        draft: (total: string) =>
            `Het concept van ${total} wordt bij versturen definitief: het krijgt het volgende factuurnummer en de datum van vandaag. Daarna pas je het niet meer aan.`,
        issued: (number: string, total: string) =>
            `Factuur ${number} van ${total} gaat mee zoals ze is.`,
        onlyClient:
            'Alleen de klant krijgt de factuur; de andere ontvangers krijgen alleen het rapport.',
        noClient:
            'Er staat geen klant met een e-mailadres tussen de ontvangers. De factuur gaat alleen naar de klant en gaat nu dus niet mee.',
        off: 'Uit: het rapport gaat zonder factuur.',
        withoutMessage:
            'Zonder bericht aan de klant gaat er ook geen factuur mee. Stuur je een bericht, dan kan de factuur mee.',
        delivered: (number: string, at: string | null) =>
            at === null
                ? `De klant heeft factuur ${number} al. Ze gaat niet nog een keer mee.`
                : `Factuur ${number} ging op ${at} al naar de klant. Ze gaat niet nog een keer mee.`,
        closed: 'De factuur van deze keuring is al betaald of gecrediteerd; er gaat geen factuur mee.',
        nothing:
            'Er valt niets te factureren: geen dienst van deze keuring heeft een prijs of een tarief. Het rapport gaat zonder factuur.',
        message: 'Eigen bericht',
        messageHint:
            'Staat in de mail aan de klant, onder de factuur. Je standaardtekst pas je aan bij Instellingen, Berichten.',
        messagePlaceholder: 'Bijvoorbeeld: Hartelijk dank voor uw opdracht.',
        messageCount: (count: number, max: number) => `${count} van ${max} tekens`,
        tooLong: (max: number) => `Je eigen bericht mag hoogstens ${max} tekens hebben.`,

        resultDrafted: (number: string, total: string) =>
            `Factuur ${number} van ${total} is gemaakt uit de diensten van de keuring en ging als pdf mee naar de klant.`,
        resultIssued: (number: string, total: string) =>
            `Het concept is definitief gemaakt als factuur ${number} van ${total} en ging als pdf mee naar de klant.`,
        resultIncluded: (number: string, total: string) =>
            `Factuur ${number} van ${total} ging als pdf mee naar de klant.`,
        resultMissing: 'Er ging geen factuur mee.',
    },

    copyLink: 'Link kopiëren',
    copyCode: 'Code kopiëren',
    copied: 'Gekopieerd',
    copyFailed: 'Kopiëren lukte niet; selecteer de tekst en kopieer hem zelf.',

    alreadyApproved:
        'Dit rapport was al goedgekeurd. Het scherm toont nu de actuele stand; een nieuwe toegangscode maak je op de klantpagina.',
    withoutRecipients:
        'Er is geen ontvanger met een e-mailadres. Vul het e-mailadres van de klant in bij Klant bewerken; het rapport neemt het meteen over.',
    notReady: 'Het concept wordt nog gemaakt. Probeer het zo opnieuw.',
    locked: 'Dit rapport is al goedgekeurd; de tekst staat vast.',
    notApproved: 'Keur het rapport eerst goed; daarna kun je het opnieuw versturen.',
    reviseDone: (version: number) =>
        `Versie ${version} staat klaar om aan te passen. De klant houdt zijn huidige versie tot je deze vaststelt.`,

    sectionsEyebrow: 'Secties',
    sectionsNote: 'Voorblad en kleuren komen uit jouw huisstijl.',
    sectionsLabel: 'Secties van het rapport',
    /** Boven de secties achterin, in de volgorde van de PDF en de klantpagina. */
    sectionsAppendix: 'Toelichting en voorwaarden',

    /**
     * De weg terug naar de opname. Hier pas je het antwoord aan waar het rapport
     * op steunt; "Tekst aanpassen" hiernaast herschrijft alleen wat er in het
     * rapport staat.
     */
    surveyJump: {
        action: 'Aanpassen in de opname',
        actionLabel: (what: string) => `${what} aanpassen in de opname`,
        /** In de smalle kolom van de bevindingentabel. */
        short: 'In de opname',
        busy: 'Opname wordt klaargezet…',
        columnSurvey: 'Opname',

        reopenTitle: 'Eerst de opname heropenen',
        reopenBody:
            'De opname is ingediend, dus daar valt nu niets aan te passen. Heropen hem: je staat dan meteen bij dit onderdeel en past het antwoord aan. Het concept blijft staan; dien je de opname opnieuw in, dan komt er een nieuwe versie.',
        /** Het rapport is al verstuurd. */
        reopenBodySent:
            'De opname gaat weer open. Het verstuurde rapport blijft staan tot je een nieuwe versie vaststelt; daarna ziet de klant die via dezelfde link. Je staat meteen bij dit onderdeel; dien de opname daarna opnieuw in.',
        reopenReason: 'Reden',
        reopenReasonPlaceholder: 'Bijvoorbeeld: het bedrag bij het kozijn klopt niet.',
        reopenReasonDefault: 'Correctie na het nalezen van het rapport.',
        reopenConfirm: 'Heropenen en naar de opname',
        reopenBusy: 'Bezig met heropenen',
        reopenCancel: 'Annuleren',
        reopenOffline: 'Heropenen kan alleen met verbinding.',

        goTitle: 'Naar de opname',
        goBody: 'De opname staat open bij de inspecteur. Je gaat er nu heen, bij dit onderdeel. Wat je daar aanpast komt in de volgende versie van het rapport.',
        goConfirm: 'Naar de opname',

        cancelledTitle: 'De keuring is geannuleerd',
        cancelledBody: 'De opname van een geannuleerde keuring gaat niet meer open.',
        notAllowedTitle: 'De opname is ingediend',
        notAllowedBody:
            'Alleen de eigenaar en de inspecteur van de opname kunnen hem heropenen. Vraag of een van hen dat doet; daarna pas je het antwoord in de opname aan.',
        failed: 'De opname kon niet geopend worden. Probeer het opnieuw.',
        noEnvironment:
            'Je kijkt mee in een ander bedrijf. De opname staat niet op dit apparaat, dus daar valt niets aan te passen.',
        close: 'Sluiten',
    },

    edit: 'Tekst aanpassen',
    editLabel: (section: string) => `Tekst van ${section}`,
    editHint:
        'Een regel die met * begint wordt een opsomming; een lege regel begint een nieuwe alinea.',
    save: 'Opslaan',
    cancel: 'Annuleren',
    restore: 'Gegenereerde tekst terugzetten',
    editedBadge: 'aangepast',
    editLocked: 'Na goedkeuring staat de tekst vast.',
    partNoBody:
        'Nog geen toelichting bij dit onderdeel. Een eigen tekst komt in de PDF bij dit onderdeel te staan.',
    nhgNoBody: 'De NHG-bijlage wordt opgebouwd uit de bevindingen en heeft geen eigen tekst.',
    guideFixed:
        'Deze uitleg staat in elk rapport vóór de kosten, met de oordelen in hun kleur. Je past hem niet aan.',

    costDirect: 'Direct',
    costTermijn: 'Op termijn',
    costOnderhoud: 'Verbetering',

    countsLine: (findings: number, defects: number, attention: number) =>
        `${findings} ${findings === 1 ? 'bevinding' : 'bevindingen'} · ${defects} ${defects === 1 ? 'gebrek' : 'gebreken'} · ${attention} ${attention === 1 ? 'aandachtspunt' : 'aandachtspunten'}`,
    partsLine: (parts: number, quiet: number) =>
        `${parts} ${parts === 1 ? 'onderdeel' : 'onderdelen'}, waarvan ${quiet} zonder bijzonderheden`,
    questionsLine: (answered: number, visible: number) =>
        `${answered} van ${visible} ${visible === 1 ? 'vraag' : 'vragen'} beantwoord`,

    tableLabel: 'Bevindingen met kosten',
    tableNote: 'Hier staan de bevindingen met kosten; alle bevindingen staan in de PDF.',
    columnPart: 'Bouwdeel',
    columnFinding: 'Bevinding',
    columnUrgency: 'Urgentie',
    columnCost: 'Kosten',
    findingsEmpty: 'Geen bevindingen met kosten.',

    photosLabel: "Foto's",
    logoCaption: 'logo',
    reportHeading: (kind: string, norm: string) => [kind, norm].filter(Boolean).join(' · '),
    reportMeta: (client: string, date: string | null, inspector: string) =>
        [
            client ? `Klant ${client}` : null,
            date ? `Inspectie ${date}` : null,
            inspector ? `Inspecteur ${inspector}` : null,
        ]
            .filter(Boolean)
            .join(' · '),

    /**
     * Het rapport over het hele scherm, zoals de koper het ziet. Het documentvlak
     * op dit scherm is een werkvlak; dit is de leesweergave.
     */
    view: {
        open: 'Rapport bekijken',
        /** Op de klantpagina, waar dezelfde pagina in een venster met een kolom ernaast staat. */
        openFull: 'Volledig scherm',
        back: 'Terug naar het rapport',
        backShort: 'Rapport',
        menu: 'Menu',
        eyebrow: (version: number) => `Zoals de koper het ziet · versie ${version}`,
        loading: 'Rapport',
        errorTitle: 'Het rapport kon niet geladen worden',
        noneTitle: 'Er is nog niets te bekijken',
        noneBody:
            'Zodra er een concept is, zie je hier het rapport zoals de koper het krijgt: dezelfde pagina, met de tabbladen Overzicht, Kosten en Rapport.',
        generatingTitle: 'Het concept wordt gemaakt',
        generatingBody: 'Zodra het klaar is, staat het hier. Ga terug en probeer het zo opnieuw.',
        failedTitle: 'Het concept kon niet gemaakt worden',
        failedBody: 'Ga terug naar het rapport en maak het opnieuw.',
        pdfBusy: 'De PDF wordt opgehaald…',
        pdfFailed: 'De PDF kon niet worden opgehaald. Probeer het zo opnieuw.',
    },

    errorTitle: 'Het rapport kon niet geladen worden',

    surveyAwaitsInspectorTitle: 'Wacht op akkoord van de inspecteur',
    awaitingInspectorOffice: (count: number) =>
        `${count === 1 ? 'Eén aangepast bedrag wacht' : `${count} aangepaste bedragen wachten`} op akkoord van de inspecteur. De inspecteur ondertekent de NHG-bijlage; goedkeuren kan pas als de inspecteur akkoord is.`,
    awaitingInspectorSelf: (count: number) =>
        `Een collega paste ${count === 1 ? 'een bedrag' : `${count} bedragen`} aan. Geef akkoord bij Kosten, of pas het bedrag zelf aan. Daarna kan het rapport goedgekeurd worden.`,
    awaitingInspectorApprove:
        'De inspecteur is nog niet akkoord met een aangepast bedrag. Keur het rapport goed zodra de inspecteur akkoord is.',
    adjustSurveyOpen:
        'De opname staat weer open. Pas het bedrag aan in de opname, of hier zodra de opname opnieuw is ingediend.',

    costs: {
        eyebrow: 'Kosten & NHG',
        title: 'Kosten en NHG-bijlage',
        introEditable:
            'Zo komen de bedragen in het rapport en in de NHG-bijlage. Klopt een bedrag niet, pas het dan aan met een reden: het concept verandert meteen mee.',
        introReadOnly: 'Zo staan de bedragen in het rapport en in de NHG-bijlage.',
        introLocked:
            'Zo staan de bedragen in deze versie. Na goedkeuring staan ze vast; een correctie maak je in een nieuwe versie.',
        introSurveyOpen:
            'De opname staat weer open bij de inspecteur. Bedragen pas je nu in de opname aan.',

        whereTitle: 'Waar de kosten staan',
        whereLabel: 'Kosten in het rapport en in de NHG-bijlage',
        whereReport: 'In het rapport',
        whereAnnex: 'In de NHG-bijlage',
        whereOutside: 'Buiten de bijlage',
        whereOutsideCount: (count: number) =>
            count === 0
                ? 'geen bevindingen'
                : `${count} ${count === 1 ? 'bevinding' : 'bevindingen'}`,
        columnDirect: 'Direct',
        columnTerm: 'Op termijn',
        columnImprovement: 'Verbetering',

        annexOff: 'De NHG-bijlage staat uit voor dit rapport.',
        annexTitle: 'NHG-bijlage',
        annexNote:
            'Model bouwkundig rapport NHG: direct noodzakelijk en op termijn noodzakelijk per onderdeel, zoals in de PDF. Kies een regel om te zien welke bevindingen erachter zitten.',
        columnCode: 'Code',
        columnFindings: 'Bevindingen',
        columnLocations: 'Locatie',
        columnNecessaryDirect: 'Direct noodzakelijk',
        columnNecessaryTerm: 'Op termijn noodzakelijk',
        tableTotal: (label: string) => `Totaal ${label.toLowerCase()}`,
        rowFindings: (count: number) =>
            `${count} ${count === 1 ? 'bevinding' : 'bevindingen'} achter deze regel`,
        noAmount: '—',

        besideTitle: 'Niet op een regel van de bijlage',
        outsideTitle: (count: number) => `Buiten de NHG-bijlage (${count})`,
        outsideNote:
            'Deze bevindingen staan wel in het rapport, maar niet in de NHG-bijlage. Kies een NHG-onderdeel bij Aanpassen als ze er wel in horen.',
        improvementTitle: 'Alleen verbeterkosten',
        improvementNote:
            'Verbeterkosten staan in de bijlage op de regel Verbetering, niet per onderdeel.',
        withoutAmountTitle: 'Aangepast naar geen bedrag',
        outsideReason: { no_code: 'Geen NHG-onderdeel', excluded: 'Niet opnemen' } as Record<
            string,
            string
        >,
        empty: 'Er zijn geen bevindingen met kosten.',

        manual: 'Handmatig',
        /** "Locatie: rechterzijde, tweede raam" - wat de inspecteur op locatie noteerde. */
        location: (place: string) => `Locatie: ${place}`,
        adjusted: 'aangepast',
        calculationQuantity: (quantity: string, unit: string, total: string) =>
            `${quantity} × ${unit} = ${total}`,
        calculationEntered: 'Bedrag ingetypt in de opname',
        calculationFixed: 'Vast bedrag uit de vragenlijst',
        calculationManual: 'Door de inspecteur toegevoegd, bedragen ingetypt',
        calculatedWas: (amount: string) => `berekend ${amount}`,
        nhgLine: (code: string, element: string | null) =>
            element ? `NHG ${code} ${element}` : `NHG ${code}`,
        nhgOwnCode: 'met de hand gekozen',
        nhgChoice: (label: string) => `NHG-keuze: ${label}`,
        adjustedBy: (name: string | null, at: string | null) =>
            ['Aangepast', name ? `door ${name}` : null, at ? `op ${at}` : null]
                .filter(Boolean)
                .join(' '),
        awaitsInspector: 'Wacht op akkoord inspecteur',
        confirmedBy: (name: string | null) => (name ? `Akkoord van ${name}` : 'Inspecteur akkoord'),
        byInspector: 'Door de inspecteur',
        adjust: 'Aanpassen',
        adjustLabel: (finding: string) => `Bedragen van ${finding} aanpassen`,
        confirm: 'Akkoord',
        confirmLabel: (finding: string) => `Akkoord met de aangepaste bedragen van ${finding}`,
        confirmed: 'Je akkoord staat in het rapport.',
        saved: 'Aangepast. Het concept is bijgewerkt.',
    },

    adjust: {
        title: 'Bedragen aanpassen',
        close: 'Sluiten',
        amountsTitle: 'Bedragen',
        amountsNote: 'Laat een veld leeg voor het bedrag uit de vragenlijst.',
        amountsNoteManual: 'Een leeg veld is € 0.',
        direct: 'Direct',
        term: 'Op termijn',
        improvement: 'Verbetering',
        fromQuestionnaire: (amount: string) => `Vragenlijst: ${amount}`,
        amountInvalid: "Vul een bedrag in euro's in, bijvoorbeeld 1.450 of 1.450,50.",
        nhgChoice: 'NHG-bijlage',
        nhgChoiceDefault: 'Volgens de bedragen',
        nhgCode: 'NHG-onderdeel',
        /** `undefined`: het onderdeel uit de vragenlijst is niet bekend, want er is er met de hand een gekozen. */
        nhgCodeDefault: (code?: string | null) =>
            code === undefined
                ? 'Uit de vragenlijst'
                : code
                  ? `Uit de vragenlijst (${code})`
                  : 'Uit de vragenlijst (geen)',
        reason: 'Reden',
        reasonPlaceholder: 'Bijvoorbeeld: offerte van de aannemer is lager',
        reasonMissing: 'Schrijf kort op waarom je dit aanpast.',
        awaitsNote:
            'De inspecteur ondertekent de NHG-bijlage. Je aanpassing wacht op akkoord van de inspecteur voordat het rapport goedgekeurd kan worden.',
        save: 'Opslaan',
        saving: 'Opslaan…',
        cancel: 'Annuleren',
    },
} as const;
